import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { SHEET } from '../config/constants';
import { useAlertsQuery } from '../hooks/useAlerts';
import { useIsNarrow } from '../hooks/useMediaQuery';
import { useStationsQuery } from '../hooks/useStations';
import { mapController } from '../map/mapController';
import { closeDetail, useAppStore, type SheetDetent } from '../store/appStore';
import { useViewportStore } from '../store/viewportStore';
import { GaugeDetailPanel } from '../features/gauge-detail/GaugeDetailPanel';
import { StationDetailPanel } from '../features/station-detail/StationDetailPanel';
import { AlertDetail } from './AlertDetail';
import { DetailShell } from './DetailShell';
import { SearchPanel } from './SearchPanel';
import { SearchPinDetail } from './SearchPinDetail';
import { Button, StatusMessage } from './ui';

function detentHeight(detent: SheetDetent, viewportHeight: number): number {
  switch (detent) {
    case 'small':
      return SHEET.smallPx;
    case 'medium':
      return Math.round(viewportHeight * SHEET.mediumFraction);
    case 'large':
      return Math.round(viewportHeight * SHEET.largeFraction);
  }
}

/**
 * The panel: a right-hand column on wide screens (the deployed web layout), a
 * three-detent bottom sheet on narrow screens (the iOS layout). It always
 * exists and cannot be dismissed. The map is padded so camera moves aim at
 * the uncovered part of the viewport.
 */
export function Panel() {
  const narrow = useIsNarrow();
  const detent = useAppStore((s) => s.sheetDetent);
  const setDetent = useAppStore((s) => s.setSheetDetent);
  const mapReady = useViewportStore((s) => s.mapReady);
  const ref = useRef<HTMLElement>(null);
  const drag = useRef<{ startY: number; startHeight: number } | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !mapReady) return;
    const apply = () => {
      const rect = el.getBoundingClientRect();
      if (narrow) {
        // Up through medium the map stays interactive above the sheet.
        const covered = detent === 'large' ? detentHeight('medium', window.innerHeight) : rect.height;
        mapController.setPadding({ top: 0, left: 0, right: 0, bottom: Math.min(covered, window.innerHeight * 0.6) });
      } else {
        mapController.setPadding({ top: 0, bottom: 0, left: 0, right: Math.max(0, window.innerWidth - rect.left) });
      }
    };
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    apply();
    return () => ro.disconnect();
  }, [narrow, detent, mapReady]);

  useEffect(() => {
    document.documentElement.style.setProperty('--sheet-height', narrow ? `${detentHeight(detent, window.innerHeight)}px` : '0px');
  }, [narrow, detent]);

  const onHandlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    drag.current = { startY: e.clientY, startHeight: el.getBoundingClientRect().height };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onHandlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current || !ref.current) return;
    const h = Math.max(SHEET.smallPx, drag.current.startHeight - (e.clientY - drag.current.startY));
    ref.current.style.height = `${h}px`;
  };
  const onHandlePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current || !ref.current) return;
    const h = drag.current.startHeight - (e.clientY - drag.current.startY);
    drag.current = null;
    ref.current.style.height = '';
    const vh = window.innerHeight;
    const stops: [SheetDetent, number][] = [
      ['small', detentHeight('small', vh)],
      ['medium', detentHeight('medium', vh)],
      ['large', detentHeight('large', vh)],
    ];
    stops.sort((a, b) => Math.abs(a[1] - h) - Math.abs(b[1] - h));
    setDetent(stops[0][0]);
  };

  const style = narrow ? { height: `${detentHeight(detent, typeof window !== 'undefined' ? window.innerHeight : 800)}px` } : undefined;

  return (
    <aside ref={ref} id="panel" className={`panel ${narrow ? 'panel-sheet' : 'panel-side'} detent-${detent}`} style={style} aria-label="Flood data">
      {narrow ? (
        <div
          className="drag-handle-area"
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
          role="separator"
          aria-label="Resize panel"
          aria-orientation="horizontal"
        >
          <div className="drag-handle" />
        </div>
      ) : null}
      <PanelContent />
    </aside>
  );
}

function PanelContent() {
  const view = useAppStore((s) => s.panelView);
  const { data: alerts } = useAlertsQuery();
  const { data: stations } = useStationsQuery();
  const pins = useAppStore((s) => s.searchPins);

  switch (view.kind) {
    case 'list':
      return <SearchPanel />;
    case 'alert': {
      const alert = alerts?.find((a) => a.id === view.alertId);
      return (
        <DetailShell title="Alert Detail">
          {alert ? (
            <AlertDetail alert={alert} />
          ) : (
            <StatusMessage action={<Button onClick={closeDetail}>Back</Button>}>This alert is no longer active.</StatusMessage>
          )}
        </DetailShell>
      );
    }
    case 'gauge':
      return (
        <DetailShell title="Gauge">
          <GaugeDetailPanel gauge={view.gauge} />
        </DetailShell>
      );
    case 'station': {
      const station = stations?.find((s) => s.id === view.stationId);
      return (
        <DetailShell title="Station">
          {station ? <StationDetailPanel station={station} /> : <StatusMessage>Station not found.</StatusMessage>}
        </DetailShell>
      );
    }
    case 'pin': {
      const pin = pins.find((p) => p.id === view.pinId);
      return pin ? <SearchPinDetail pin={pin} /> : <SearchPanel />;
    }
  }
}
